import { observer } from "mobx-react-lite";
import { AddressCleansingServiceModel, AddressEntity } from "../../services/address/AddressCleansingService";
import { useMemo, useState } from "react";
import { AvailableCountriesStorageModel } from "../../services/organization/AvailableCountriesStorage";
import { api } from "../../services/api/ApiService";
import { translationService } from "../../services/translation/TranslationService";
import { EditDropdownField, EditDropdownSuggestionField, EditTextField } from "../../pages/my-account/request-organization/DataComponents";
import React from "react";

export interface IAddressFieldProps {
    value: any,
    readonly?: boolean,
    setFieldValue(field: string, value: string): void,
    country: string
}

export const CountryField = observer(function CountryField(props: IAddressFieldProps) {
    const value = props.readonly ? props.value || "-" : props.value;
    const [availableCountriesStorage] = useState(() =>
        AvailableCountriesStorageModel.create({}, { api })
    );

    const availableCountries = useMemo(() => {
        return availableCountriesStorage.getAvailableCountries();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [availableCountriesStorage.isLoading, translationService.language]);

    return (
        <EditDropdownField
            id="organization-country"
            labelKey="general.organization.country"
            fieldName="country"
            name={value}
            required
            readonly={props.readonly}
            options={availableCountries}
            setSelectedItem={(item) => {
                props.setFieldValue("country", item ?? "");
                props.setFieldValue("address", "");
                props.setFieldValue("zip", "");
                props.setFieldValue("city", "");
                props.setFieldValue("state", "");
            }}
        />
    )
})

export const AddressField = observer(function AddressField(props: IAddressFieldProps) {
    const value = props.readonly ? props.value || "-" : props.value;
    const [addressCleansingService] = useState(() =>
        AddressCleansingServiceModel.create({}, { api })
    );

    return (
        <EditDropdownSuggestionField
            id="organization-address"
            labelKey="general.organization.address"
            placeholderKey="organization.EditOrganizationAddress.placeholder"
            fieldName="address"
            fieldValue={value}
            required
            readOnly={props.readonly}
            performSearch={(query) =>
                addressCleansingService.search(props.country, query)
            }
            searchTextChanged={(query) => (props.setFieldValue("address", query))}
            selectedItemChanged={(item: AddressEntity) => {
                props.setFieldValue("address", item.street);
                props.setFieldValue("zip", item.zip);
                props.setFieldValue("city", item.city);
                props.setFieldValue("state", item.state);
            }}
            clearCurrentItem={() => props.setFieldValue("address", "")}
        />
    )
})

export const AddressNumberField = observer(function AddressNumberField(props: IAddressFieldProps) {
    const value = props.readonly ? props.value || "-" : props.value;
    return (
        <EditTextField
            id="organization-address-number"
            labelKey="general.organization.address-number"
            placeholderKey="organization.EditOrganizationAddressNumber.placeholder"
            fieldName="addressNumber"
            name={value}
            required
            readonly={props.readonly}
        />
    )
})

export const ZipField = observer(function ZipField(props: IAddressFieldProps) {
    const value = props.readonly ? props.value || "-" : props.value;
    const [addressCleansingService] = useState(() =>
        AddressCleansingServiceModel.create({}, { api })
    );

    return (
        <EditDropdownSuggestionField
            id="organization-zip"
            labelKey="general.organization.zip"
            placeholderKey="organization.EditOrganizationZip.placeholder"
            fieldName="zip"
            fieldValue={value}
            required
            readOnly={props.readonly}
            performSearch={(query) =>
                addressCleansingService.search(props.country, query)
            }
            searchTextChanged={(query) => (props.setFieldValue("zip", query))}
            selectedItemChanged={(item: AddressEntity) => {
                props.setFieldValue("address", item.street);
                props.setFieldValue("zip", item.zip);
                props.setFieldValue("city", item.city);
                props.setFieldValue("state", item.state);
            }}
            clearCurrentItem={() => props.setFieldValue("zip", "")}
        />
    )
})

export const CityField = observer(function CityField(props: IAddressFieldProps) {
    const value = props.readonly ? props.value || "-" : props.value;
    const [addressCleansingService] = useState(() =>
        AddressCleansingServiceModel.create({}, { api })
    );

    return (
        <EditDropdownSuggestionField
            id="organization-city"
            labelKey="general.organization.city"
            placeholderKey="organization.EditOrganizationCity.placeholder"
            fieldName="city"
            fieldValue={value}
            required
            readOnly={props.readonly}
            performSearch={(query) =>
                addressCleansingService.search(props.country, query)
            }
            searchTextChanged={(query) => (props.setFieldValue("city", query))}
            selectedItemChanged={(item: AddressEntity) => {
                props.setFieldValue("address", item.street);
                props.setFieldValue("zip", item.zip);
                props.setFieldValue("city", item.city);
                props.setFieldValue("state", item.state);
            }}
            clearCurrentItem={() => props.setFieldValue("city", "")}
        />
    )
})

export const StateField = observer(function StateField(props: IAddressFieldProps) {
    const value = props.readonly ? props.value || "-" : props.value;
    return (
        <EditTextField
            id="organization-state"
            labelKey="general.organization.state"
            placeholderKey="organization.EditOrganizationState.placeholder"
            fieldName="state"
            readonly={props.readonly}
            name={value}
        />
    )
})

export const VatField = observer(function VatField(props: IAddressFieldProps) {
    const value = props.readonly ? props.value || "-" : props.value;
    return (
        <EditTextField
            id="organization-vatNumber"
            labelKey="general.organization.vat"
            placeholderKey="organization.EditOrganizationVAT.placeholder"
            fieldName="vat"
            readonly={props.readonly}
            name={value}
        />
    )
})